import * as Sentry from '@sentry/browser';
import { trackSessionError } from './saveLogRocketSession';

let initialized = false;

export function initSentry(dsn, tags, tunnelUrl) {
    if (initialized || !dsn) return;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return;
    Sentry.init({
        dsn,
        tunnel: tunnelUrl || undefined,
        release: `bro-builder@${process.env.APP_VERSION || 'unknown'}`,
        sendDefaultPii: false,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 0.05,
    });
    Sentry.setTag('hostname', window.location.hostname);
    Sentry.setTag('url', window.location.href);
    if (tags) {
        for (const [key, value] of Object.entries(tags)) {
            if (value != null) Sentry.setTag(key, value);
        }
    }
    initialized = true;
}

export function captureError(error, context) {
    if (!initialized) return;
    Sentry.withScope(scope => {
        // Attach LogRocket session URL so every Sentry issue links directly to the replay
        try {
            const lrUrl = window.LogRocket && window.LogRocket.sessionURL;
            if (lrUrl && typeof lrUrl === 'string' && lrUrl.startsWith('https://')) {
                // Set as structured context (visible in Sentry sidebar) and as extra for search
                scope.setContext('logrocket', { sessionURL: lrUrl });
                scope.setExtra('logRocketSession', lrUrl);
            } else if (window.LogRocket && typeof window.LogRocket.getSessionURL === 'function') {
                window.LogRocket.getSessionURL((url) => {
                    if (url) Sentry.setExtra('logRocketSession', url);
                });
            }
        } catch (e) { /* ignore */ }
        if (context) {
            scope.setExtras(context);
        }
        Sentry.captureException(error);
    });
    // Track this error in the session accumulator so saveLogRocketSession includes it
    try {
        const sentryEventId = Sentry.lastEventId();
        trackSessionError({
            errorType: context?.type || null,
            context: context ? JSON.stringify(context) : null,
            message: typeof error === 'string' ? error : (error?.message || null),
            sentryEventId: sentryEventId || null,
        });
    } catch (e) { /* ignore */ }
}

/**
 * Returns the last Sentry event ID captured in this session, or null.
 */
export function getLastSentryEventId() {
    if (!initialized) return null;
    try {
        return Sentry.lastEventId() || null;
    } catch (e) {
        return null;
    }
}

/**
 * Send an info-level diagnostic message to Sentry.
 * Use for tracing NMI lifecycle events so we can see exactly what happened
 * in Sentry's issue stream without needing LogRocket or console access.
 *
 * @param {string} message - Short message (becomes the Sentry issue title)
 * @param {object} context - Key/value pairs attached as extras on the event
 */
export function captureInfo(message, context) {
    if (!initialized) return;
    Sentry.withScope(scope => {
        scope.setLevel('info');
        try {
            const lrUrl = window.LogRocket && window.LogRocket.sessionURL;
            if (lrUrl && typeof lrUrl === 'string' && lrUrl.startsWith('https://')) {
                scope.setContext('logrocket', { sessionURL: lrUrl });
                scope.setExtra('logRocketSession', lrUrl);
            }
        } catch (e) { /* ignore */ }
        if (context) {
            scope.setExtras(context);
        }
        Sentry.captureMessage(message);
    });
}
